import '../sass/blocks/catalog.scss';
import { initCatalogFilters } from './utils/catalog-filters';

const catalog = document.querySelector('[data-catalog]');
const messages = window.ovaliaCatalog;

const initCategoryNavigation = () => {
    const navigation = catalog.querySelector('.Catalog__navigation');
    const current = navigation?.querySelector('[aria-current="page"]');
    if (!current) return;

    window.requestAnimationFrame(() => {
        if (getComputedStyle(navigation).overflowX !== 'auto' || navigation.scrollWidth <= navigation.clientWidth) return;
        const rail = navigation.getBoundingClientRect();
        const item = current.getBoundingClientRect();
        // Move only the category rail, preserving the page's restored scroll position.
        navigation.scrollLeft += item.left - rail.left - (navigation.clientWidth - item.width) / 2;
    });
};

const initSorting = () => {
    const form = catalog.querySelector('[data-catalog-sort]');
    if (!form) return;
    form.classList.add('CatalogSort--enhanced');
    form.querySelector('select').addEventListener('change', () => form.requestSubmit());
};

const initPagination = () => {
    const grid = catalog.querySelector('[data-catalog-grid]');
    const more = catalog.querySelector('[data-catalog-more]');
    const status = catalog.querySelector('[data-catalog-status]');
    if (!grid || !more || !status || !messages) return;
    const label = more.textContent;
    let loading = false;
    let loadedPage = Number(catalog.dataset.page);

    more.addEventListener('click', async (event) => {
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        if (loading) return;
        loading = true;
        more.setAttribute('aria-disabled', 'true');
        grid.setAttribute('aria-busy', 'true');
        more.textContent = messages.loading;
        // Keep routine announcements available to screen readers without a second visible counter.
        status.classList.add('Catalog__sr-only');
        status.textContent = messages.loading;
        const timeout = new AbortController();
        const timer = window.setTimeout(() => timeout.abort(), 20000);
        try {
            const url = new URL(more.href, window.location.href);
            if (url.origin !== window.location.origin) throw new Error('Unexpected page origin');
            const response = await fetch(url.href, { credentials: 'same-origin', signal: timeout.signal });
            if (!response.ok) throw new Error(`HTTP ${response.status}`);
            const documentNext = new DOMParser().parseFromString(await response.text(), 'text/html');
            const nextCatalog = documentNext.querySelector('[data-catalog]');
            const nextGrid = nextCatalog?.querySelector('[data-catalog-grid]');
            const page = Number(nextCatalog?.dataset.page);
            if (!nextGrid || page !== loadedPage + 1 || !nextGrid.querySelector('[data-catalog-product]')) {
                throw new Error('Invalid catalogue page');
            }
            const existing = new Set([...grid.querySelectorAll('[data-catalog-product]')].map((item) => item.dataset.catalogProduct));
            const fragment = document.createDocumentFragment();
            let firstNewLink = null;
            [...nextGrid.children].forEach((item) => {
                if (item.dataset.catalogProduct && existing.has(item.dataset.catalogProduct)) return;
                // Ignore scripts even if an extension added them to a card.
                item.querySelectorAll('script').forEach((script) => script.remove());
                firstNewLink ||= item.querySelector('a');
                fragment.append(item);
            });
            grid.append(fragment);
            loadedPage = page;
            const total = Number(nextCatalog.dataset.total);
            const shown = Math.min(total, grid.querySelectorAll('[data-catalog-product]').length);
            catalog.dataset.shown = String(shown);
            catalog.dataset.total = String(total);
            const progressLabel = messages.progress.replace('{shown}', shown.toLocaleString('pl-PL')).replace('{total}', total.toLocaleString('pl-PL'));
            catalog.querySelector('[data-catalog-progress-label]').textContent = progressLabel;
            const progress = catalog.querySelector('[data-catalog-progress]');
            progress.max = Math.max(1, total);
            progress.value = shown;
            status.textContent = progressLabel;
            const next = nextCatalog.querySelector('[data-catalog-more]');
            if (next) more.href = next.href;
            else more.hidden = true;
            // Keyboard users continue at the first newly appended card, without moving the viewport.
            firstNewLink?.focus({ preventScroll: true });
        } catch (error) {
            status.classList.remove('Catalog__sr-only');
            status.textContent = `${messages.error} `;
            const fallback = document.createElement('a');
            fallback.href = more.href;
            fallback.textContent = messages.nextPage;
            status.append(fallback);
        } finally {
            window.clearTimeout(timer);
            loading = false;
            more.removeAttribute('aria-disabled');
            grid.removeAttribute('aria-busy');
            more.textContent = label;
        }
    });
};

if (catalog) {
    initCategoryNavigation();
    initCatalogFilters(catalog, messages);
    initSorting();
    initPagination();
}
